import React, { useState } from 'react';
import {
  Plus,
  Workflow as WorkflowIcon,
  AlertCircle,
  Sparkles,
  Zap,
  Bot,
  GitBranch,
  Clock,
  ArrowRight,
  Lightbulb,
  Play,
  Link2,
} from 'lucide-react';
import {
  useWorkflows,
  useTriggerWorkflow,
  useDeleteWorkflow,
  useUpdateWorkflow,
  type Workflow,
} from '../helpers/useWorkflowsApi';
import { Button } from './Button';
import { Input } from './Input';
import { WorkflowItem } from './WorkflowItem';
import { Skeleton } from './Skeleton';
import { toast } from 'sonner';
import styles from './WorkflowListPanel.module.css';

interface WorkflowListPanelProps {
  sessionId?: string;
  className?: string;
  onCreateNew?: () => void;
  onGenerateWithAI?: () => void;
  onEditWorkflow?: (workflow: Workflow) => void;
  onSelectWorkflow?: (workflow: Workflow) => void;
  selectedWorkflowId?: string | null;
}

export const WorkflowListPanel: React.FC<WorkflowListPanelProps> = ({
  sessionId,
  className,
  onCreateNew,
  onGenerateWithAI,
  onEditWorkflow,
  onSelectWorkflow,
  selectedWorkflowId,
}) => {
  const [runningWorkflowId, setRunningWorkflowId] = useState<string | null>(null);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editActive, setEditActive] = useState(false);
  const [editTags, setEditTags] = useState('');

  const { data, isLoading, error, refetch } = useWorkflows(sessionId);
  const triggerMutation = useTriggerWorkflow();
  const deleteMutation = useDeleteWorkflow();
  const updateMutation = useUpdateWorkflow();

  const workflows = data?.workflows || [];

  const startEditing = (workflow: Workflow) => {
    setEditingWorkflow(workflow);
    setEditName(workflow.name);
    setEditDescription(workflow.description || '');
    setEditActive(workflow.active);
    setEditTags(workflow.tags.join(', '));
  };

  const cancelEditing = () => {
    setEditingWorkflow(null);
  };

  const saveEditing = async () => {
    if (!editingWorkflow) return;
    try {
      await updateMutation.mutateAsync({
        id: editingWorkflow.id,
        name: editName.trim() || editingWorkflow.name,
        description: editDescription.trim() || undefined,
        active: editActive,
        tags: editTags.split(',').map(t => t.trim()).filter(Boolean),
      });
      toast.success(`Workflow "${editName}" updated`);
      setEditingWorkflow(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update workflow');
    }
  };

  const handleRun = async (workflow: Workflow) => {
    setRunningWorkflowId(workflow.id);
    try {
      await triggerMutation.mutateAsync({ workflowId: workflow.id });
      toast.success(`Workflow "${workflow.name}" triggered successfully`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to trigger workflow');
    } finally {
      setRunningWorkflowId(null);
    }
  };

  const handleEdit = (workflow: Workflow) => {
    if (onEditWorkflow) {
      onEditWorkflow(workflow);
    } else {
      // Inline editing when no external editor is provided (e.g., settings tab)
      startEditing(workflow);
    }
  };

  const handleDelete = async (workflow: Workflow) => {
    if (!confirm(`Are you sure you want to delete "${workflow.name}"?`)) return;

    try {
      await deleteMutation.mutateAsync(workflow.id);
      toast.success(`Workflow "${workflow.name}" deleted`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete workflow');
    }
  };

  return (
    <div className={`${styles.panel} ${className || ''}`}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <WorkflowIcon className={styles.headerIcon} />
          <h2>Workflows</h2>
          <span className={styles.countBadge}>{workflows.length}</span>
        </div>
        <div className={styles.headerActions}>
          <Button
            size="sm"
            variant="outline"
            onClick={onGenerateWithAI}
            className={styles.generateButton}
          >
            <Sparkles size={14} />
            AI Generate
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={onCreateNew}
            aria-label="Create new workflow"
          >
            <Plus size={18} />
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <Skeleton className={styles.skeletonItem} />
            <Skeleton className={styles.skeletonItem} />
            <Skeleton className={styles.skeletonItem} />
          </div>
        ) : error || workflows.length === 0 ? (
          <div className={styles.emptyState}>
            {/* Tutorial Header */}
            <div className={styles.tutorialHeader}>
              <WorkflowIcon size={40} className={styles.tutorialIcon} />
              <h3 className={styles.tutorialTitle}>Welcome to Workflows</h3>
              <p className={styles.tutorialSubtitle}>
                Automate tasks and connect your apps with AI-powered workflows
              </p>
            </div>

            {/* Quick Start Guide */}
            <div className={styles.tutorialSection}>
              <h4 className={styles.sectionTitle}>
                <Lightbulb size={16} />
                Getting Started
              </h4>
              <div className={styles.tutorialSteps}>
                <div className={styles.tutorialStep}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepContent}>
                    <strong>Create a Workflow</strong>
                    <p>
                      Click "AI Generate" to describe what you want to automate, or "Create
                      manually" to build from scratch.
                    </p>
                  </div>
                </div>
                <div className={styles.tutorialStep}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepContent}>
                    <strong>Add Triggers & Actions</strong>
                    <p>Choose what starts your workflow (trigger) and what actions to perform.</p>
                  </div>
                </div>
                <div className={styles.tutorialStep}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepContent}>
                    <strong>Test & Activate</strong>
                    <p>Test your workflow, then activate it to run automatically.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* What You Can Do */}
            <div className={styles.tutorialSection}>
              <h4 className={styles.sectionTitle}>
                <Zap size={16} />
                What You Can Automate
              </h4>
              <div className={styles.featureGrid}>
                <div className={styles.featureItem}>
                  <Clock size={20} className={styles.featureIcon} />
                  <span>Schedule tasks</span>
                </div>
                <div className={styles.featureItem}>
                  <Bot size={20} className={styles.featureIcon} />
                  <span>AI processing</span>
                </div>
                <div className={styles.featureItem}>
                  <Link2 size={20} className={styles.featureIcon} />
                  <span>API integrations</span>
                </div>
                <div className={styles.featureItem}>
                  <GitBranch size={20} className={styles.featureIcon} />
                  <span>Conditional logic</span>
                </div>
              </div>
            </div>

            {/* Example Workflows */}
            <div className={styles.tutorialSection}>
              <h4 className={styles.sectionTitle}>
                <Play size={16} />
                Example Ideas
              </h4>
              <div className={styles.exampleList}>
                <div className={styles.exampleItem}>
                  <ArrowRight size={14} />
                  <span>"When a file is uploaded, run AI analysis and create a summary"</span>
                </div>
                <div className={styles.exampleItem}>
                  <ArrowRight size={14} />
                  <span>"Every day at 9am, fetch news and generate a digest"</span>
                </div>
                <div className={styles.exampleItem}>
                  <ArrowRight size={14} />
                  <span>"When a task is completed, notify team on Slack"</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.tutorialActions}>
              <Button size="md" onClick={onGenerateWithAI} className={styles.primaryAction}>
                <Sparkles size={16} />
                Generate with AI
              </Button>
              <Button size="md" variant="outline" onClick={onCreateNew}>
                <Plus size={16} />
                Create Manually
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.list}>
            {/* Inline edit form */}
            {editingWorkflow && (
              <div className={styles.inlineEditForm}>
                <h4 className={styles.inlineEditTitle}>Edit Workflow</h4>
                <div className={styles.inlineEditFields}>
                  <div className={styles.inlineEditField}>
                    <label>Name</label>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Workflow name" />
                  </div>
                  <div className={styles.inlineEditField}>
                    <label>Description</label>
                    <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Optional description" />
                  </div>
                  <div className={styles.inlineEditField}>
                    <label>Tags (comma-separated)</label>
                    <Input value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="e.g., daily, leads, automation" />
                  </div>
                  <div className={styles.inlineEditField}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                      Active
                    </label>
                  </div>
                </div>
                <div className={styles.inlineEditActions}>
                  <Button size="sm" onClick={saveEditing} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={cancelEditing}>Cancel</Button>
                </div>
              </div>
            )}
            {workflows.map((workflow) => (
              <WorkflowItem
                key={workflow.id}
                workflow={workflow}
                onRun={handleRun}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onClick={onSelectWorkflow || handleEdit}
                isRunning={runningWorkflowId === workflow.id}
                isSelected={selectedWorkflowId === workflow.id || editingWorkflow?.id === workflow.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
